/**
 * Especificações Técnicas e Tolerâncias Oficiais: Honda ADV 150 (2023/2024 - K0W)
 * Padrão Concessionária Honda Dream & Filosofia Fábio Akita (Zero Stubs)
 */

export interface TorqueSpec {
  id: string;
  component: string;
  category: "motor" | "cvt" | "chassi_freios";
  threadSize: string;
  socketSize: string;
  torqueNm: number;
  torqueKgfm: number;
  criticalNotes: string;
}

export interface ServiceLimitSpec {
  id: string;
  item: string;
  standardValue: string;
  serviceLimit: string;
  unit: string;
  mandatoryAction: string;
}

export interface FluidCapacitySpec {
  id: string;
  fluidName: string;
  officialStandard: string;
  drainCapacity: string;
  disassemblyCapacity: string;
  intervalKm: number;
}

export interface OEMPartNumberSpec {
  id: string;
  partNumber: string;
  name: string;
  category: "chassi" | "suspensao" | "motor" | "cvt" | "freios" | "eletrica" | "consumiveis";
  description: string;
  applicationNotes?: string;
  subsystem?:
    | "motor"
    | "cvt"
    | "pgmfi"
    | "chassi"
    | "freios"
    | "suspensao"
    | "eletrica"
    | "consumiveis";
  hotspotId?: string;
  explodedViewId?: string;
  nominalDimensionMm?: number;
  serviceLimitMm?: number;
  torqueSpec?: string;
  requiredTool?: string;
  criticalGotcha?: string;
}

export interface SpecialToolSpec {
  id: string;
  codeOrStandard: string;
  name: string;
  category: "ferramenta_especial_honda" | "instrumento_metrologia" | "ferramenta_manual";
  application: string;
  criticalUsageNote: string;
}

export interface CvtDurabilitySpec {
  id: string;
  component: string;
  partNumber: string;
  officialDurabilityKm: number;
  severeDurabilityKm: string;
  standardValue: string;
  serviceLimit: string;
  unit: string;
  symptoms: string;
  preventiveAction: string;
}

export interface VehicleIdentitySpec {
  owner: {
    name: string;
    cpfMasked: string;
    cityState: string;
  };
  registration: {
    plate: string;
    renavam: string;
    crv: string;
    claSecurityCode: string;
    category: string;
    speciesType: string;
    fuel: string;
    color: string;
    grossWeightKg: number;
    capacity: string;
    issueDate: string;
    exerciseYear: number;
  };
  vehicle: {
    brandModelVersion: string;
    projectCode: string;
    modelCode: string;
    vin: string;
    vinDecoded: {
      wmi: string; // 9C2 (Moto Honda da Amazônia)
      vds: string; // KF430 (ADV 150 Brasil)
      checkDigit: string; // 0
      modelYearCode: string; // R (2024)
      plantCode: string; // R (Manaus)
      serialNumber: string; // 003710
    };
    engineNumber: string;
    engineFamily: string; // KF43E
    manufactureYear: number; // 2023
    modelYear: number; // 2024
    displacementCc: number; // 149.3
    maxPowerCv: number; // 13.2
  };
}

export const ADV150_TORQUE_SPECS: TorqueSpec[] = [
  // Motor e Cárter
  {
    id: "t-bujão-oleo-motor",
    component: "Bujão de Dreno do Óleo do Motor",
    category: "motor",
    threadSize: "M12",
    socketSize: "Sextavado 12 mm",
    torqueNm: 24,
    torqueKgfm: 2.4,
    criticalNotes: "Trocar arruela de esmagamento de 12 mm a cada drenagem.",
  },
  {
    id: "t-tampa-tela-oleo",
    component: "Tampa da Tela de Filtragem de Óleo (Peneira)",
    category: "motor",
    threadSize: "M30 x 1.5",
    socketSize: "Sextavado 17 mm",
    torqueNm: 20,
    torqueKgfm: 2.0,
    criticalNotes: "Lubrificar anel O-ring com óleo novo. Não apertar além do torque.",
  },
  {
    id: "t-bujão-redutor-final",
    component: "Bujão de Dreno e Nível da Transmissão Final",
    category: "motor",
    threadSize: "M8",
    socketSize: "Sextavado 12 mm",
    torqueNm: 13,
    torqueKgfm: 1.3,
    criticalNotes: "Substituir as 2 arruelas de vedação de 8 mm.",
  },
  {
    id: "t-vela-ignicao",
    component: "Vela de Ignição (NGK MR8K-9)",
    category: "motor",
    threadSize: "M10",
    socketSize: "Chave de Vela 16 mm",
    torqueNm: 16,
    torqueKgfm: 1.6,
    criticalNotes: "Rosquear totalmente à mão antes de aplicar o torquímetro.",
  },
  {
    id: "t-tampa-cabecote",
    component: "Parafusos da Tampa do Cabeçote",
    category: "motor",
    threadSize: "M6",
    socketSize: "Sextavado 10 mm",
    torqueNm: 10,
    torqueKgfm: 1.0,
    criticalNotes: "Aperto cruzado uniforme; inspecionar junta de borracha.",
  },
  {
    id: "t-contraporca-valvula",
    component: "Contraporca de Ajuste da Folga de Válvula",
    category: "motor",
    threadSize: "M5",
    socketSize: "Estria 9 mm",
    torqueNm: 10,
    torqueKgfm: 1.0,
    criticalNotes: "Manter o parafuso de regulagem quadrado apoiado ao torquear.",
  },
  {
    id: "t-dreno-coolant",
    component: "Parafuso de Dreno da Bomba d’Água",
    category: "motor",
    threadSize: "M6",
    socketSize: "Sextavado 8/10 mm",
    torqueNm: 10,
    torqueKgfm: 1.0,
    criticalNotes: "Substituir arruela de vedação de cobre.",
  },

  // Transmissão CVT
  {
    id: "t-polia-primaria",
    component: "Porca da Polia Primária (Virabrequim / Variador)",
    category: "cvt",
    threadSize: "M14",
    socketSize: "Soquete 22 mm",
    torqueNm: 59,
    torqueKgfm: 6.0,
    criticalNotes: "Travar com chave em Y; afundar correia na secundária para não prensá-la.",
  },
  {
    id: "t-campana-embreagem",
    component: "Porca da Campana de Embreagem (Secundária)",
    category: "cvt",
    threadSize: "M12",
    socketSize: "Soquete 19 mm",
    torqueNm: 49,
    torqueKgfm: 5.0,
    criticalNotes: "Travar com ferramenta fixadora de pinos.",
  },
  {
    id: "t-porca-mola-secundaria",
    component: "Porca Central da Sapata de Embreagem / Mola",
    category: "cvt",
    threadSize: "M28 x 1.0",
    socketSize: "Soquete fino 39/41 mm",
    torqueNm: 54,
    torqueKgfm: 5.5,
    criticalNotes: "Comprimir mola em prensa de bancada antes de soltar ou apertar.",
  },
  {
    id: "t-tampa-cvt",
    component: "Parafusos da Tampa da Carcaça do CVT",
    category: "cvt",
    threadSize: "M6",
    socketSize: "Sextavado 8 mm",
    torqueNm: 10,
    torqueKgfm: 1.0,
    criticalNotes: "Aperto em cruz em duas etapas; respeitar comprimentos 35 mm e 40 mm.",
  },

  // Chassi, Freios e Rodas
  {
    id: "t-eixo-dianteiro",
    component: "Porca do Eixo da Roda Dianteira",
    category: "chassi_freios",
    threadSize: "M12",
    socketSize: "Soquete 17/19 mm",
    torqueNm: 59,
    torqueKgfm: 6.0,
    criticalNotes: "Apoiar o contra-eixo para não girar.",
  },
  {
    id: "t-eixo-traseiro",
    component: "Porca do Eixo da Roda Traseira",
    category: "chassi_freios",
    threadSize: "M16",
    socketSize: "Soquete 24 mm",
    torqueNm: 118,
    torqueKgfm: 12.0,
    criticalNotes: "Travar alavanca de freio traseiro; instalar cupilha/trava nova.",
  },
  {
    id: "t-caliper-dianteiro",
    component: "Parafusos de Fixação do Cáliper Dianteiro",
    category: "chassi_freios",
    threadSize: "M10",
    socketSize: "Soquete 12 mm",
    torqueNm: 30,
    torqueKgfm: 3.1,
    criticalNotes: "Aplicar trava química média azul.",
  },
  {
    id: "t-caliper-traseiro",
    component: "Parafusos de Fixação do Cáliper Traseiro",
    category: "chassi_freios",
    threadSize: "M8",
    socketSize: "Soquete 10/12 mm",
    torqueNm: 22,
    torqueKgfm: 2.2,
    criticalNotes: "Aplicar trava química média azul.",
  },
  {
    id: "t-purga-sangria-freio",
    component: "Válvula de Sangria de Freio (Purga)",
    category: "chassi_freios",
    threadSize: "M8",
    socketSize: "Estria 8 mm",
    torqueNm: 5.4,
    torqueKgfm: 0.55,
    criticalNotes: "Torquímetro de precisão baixa escala; nunca apertar em demasia.",
  },
  {
    id: "t-amortecedor-traseiro-sup",
    component: "Parafuso Superior do Amortecedor Traseiro Showa",
    category: "chassi_freios",
    threadSize: "M10",
    socketSize: "Sextavado 14 mm",
    torqueNm: 40,
    torqueKgfm: 4.1,
    criticalNotes: "Rosquear com a bucha de borracha alinhada.",
  },
  {
    id: "t-amortecedor-traseiro-inf",
    component: "Parafuso Inferior do Amortecedor Traseiro Showa",
    category: "chassi_freios",
    threadSize: "M8",
    socketSize: "Sextavado 12 mm",
    torqueNm: 27,
    torqueKgfm: 2.8,
    criticalNotes: "Fixação à balança de alumínio / carcaça; aperto uniforme.",
  },
  {
    id: "t-mesa-inferior-garfo",
    component: "Parafusos da Mesa Inferior do Garfo Dianteiro Showa",
    category: "chassi_freios",
    threadSize: "M10",
    socketSize: "Sextavado 14 mm",
    torqueNm: 27,
    torqueKgfm: 2.8,
    criticalNotes:
      "4 parafusos flangeados; aperto alternado em dois estágios para evitar empenamento da mesa.",
  },
  {
    id: "t-pino-pastilhas",
    component: "Pino de Retenção das Pastilhas de Freio (Pad Pin)",
    category: "chassi_freios",
    threadSize: "M10",
    socketSize: "Chave Allen 5 mm",
    torqueNm: 17,
    torqueKgfm: 1.7,
    criticalNotes:
      "Guia das placas de apoio Nissin; quebrar torque antes de soltar o cáliper do garfo/balança.",
  },
  {
    id: "t-bujao-pino-pastilhas",
    component: "Bujão Tampão de Proteção do Pino de Pastilhas",
    category: "chassi_freios",
    threadSize: "M10",
    socketSize: "Fenda plana larga",
    torqueNm: 2.5,
    torqueKgfm: 0.25,
    criticalNotes:
      "Vedação contra poeira e água; torque suave para não danificar o rasgo da fenda.",
  },
  {
    id: "t-tampa-reservatorio-freio",
    component: "Parafusos da Tampa do Reservatório do Cilindro Mestre",
    category: "chassi_freios",
    threadSize: "M4",
    socketSize: "Chave cruzada JIS #2",
    torqueNm: 1.5,
    torqueKgfm: 0.15,
    criticalNotes:
      "Aperto simétrico suave com chave JIS Vessel; proibido usar chave Phillips DIN comum (risco de cam-out).",
  },
  {
    id: "t-banjo-flexivel-freio",
    component: "Parafuso de Óleo do Flexível de Freio (Banjo)",
    category: "chassi_freios",
    threadSize: "M10",
    socketSize: "Sextavado 12 mm",
    torqueNm: 34,
    torqueKgfm: 3.5,
    criticalNotes: "Substituir as 2 arruelas de vedação de cobre a cada desmontagem.",
  },
  {
    id: "t-capa-defletora-radiador",
    component: "Parafusos da Capa Defletora do Radiador Lateral",
    category: "motor",
    threadSize: "M6",
    socketSize: "Sextavado 8 mm / Phillips JIS",
    torqueNm: 9,
    torqueKgfm: 0.9,
    criticalNotes: "Fixação da capa externa plástica sobre o núcleo de aletas de refrigeração.",
  },
  {
    id: "t-bau-bagagem",
    component: "Parafusos de Fixação do Baú de Bagagem (Luggage Box 27L)",
    category: "motor",
    threadSize: "M6",
    socketSize: "Sextavado 10 mm longo",
    torqueNm: 10,
    torqueKgfm: 1.0,
    criticalNotes: "4 parafusos flangeados no fundo do compartimento sob o assento.",
  },
  {
    id: "t-assoalho-allen",
    component: "Parafusos Allen de Fixação dos Painéis do Assoalho",
    category: "chassi_freios",
    threadSize: "M6",
    socketSize: "Chave Allen 5 mm",
    torqueNm: 4.2,
    torqueKgfm: 0.42,
    criticalNotes:
      "Localizados sob os tapetes de borracha; aperto de ressalto metálico cilíndrico.",
  },
  {
    id: "t-sonda-lambda",
    component: "Sensor de Oxigênio Aquecido (Sonda Lambda no Escape)",
    category: "motor",
    threadSize: "M12 x 1.25",
    socketSize: "Chave especial de sonda 17 mm",
    torqueNm: 25,
    torqueKgfm: 2.5,
    criticalNotes: "Rosqueado no tubo de escape; aperto a frio com cuidado no chicote.",
  },
  {
    id: "t-coletor-tbi",
    component: "Parafusos de Fixação do Corpo TBI / Coletor de Admissão",
    category: "motor",
    threadSize: "M6",
    socketSize: "Sextavado 10 mm",
    torqueNm: 10,
    torqueKgfm: 1.0,
    criticalNotes:
      "Aperto uniforme em cruz; certificar-se da integridade do anel O-ring de vedação.",
  },
  {
    id: "t-valvula-iacv",
    component: "Parafusos de Fixação da Válvula IACV no Corpo de Borboleta",
    category: "motor",
    threadSize: "M4",
    socketSize: "Torx / Phillips JIS",
    torqueNm: 2.1,
    torqueKgfm: 0.21,
    criticalNotes: "Fixação da carcaça do motor de passo; lubrificar O-ring com graxa de silicone.",
  },
];

export const ADV150_SERVICE_LIMITS: ServiceLimitSpec[] = [
  {
    id: "lim-correia-cvt",
    item: "Largura da Correia Dentada (V-Belt)",
    standardValue: "19,5 mm",
    serviceLimit: "18,5 mm",
    unit: "mm",
    mandatoryAction: "Substituir imediatamente por peça original 23100-K97-T01.",
  },
  {
    id: "lim-roletes-cvt",
    item: "Diâmetro Externo dos Roletes (6 un x 21g)",
    standardValue: "20,0 mm",
    serviceLimit: "19,5 mm",
    unit: "mm",
    mandatoryAction: "Substituir o jogo completo de 6 roletes. Trabalham 100% a seco.",
  },
  {
    id: "lim-campana-cvt",
    item: "Diâmetro Interno da Campana de Embreagem",
    standardValue: "125,0 mm",
    serviceLimit: "125,5 mm",
    unit: "mm",
    mandatoryAction: "Substituir a campana se ultrapassar o limite ou apresentar ranhuras.",
  },
  {
    id: "lim-sapatas-cvt",
    item: "Espessura da Lona das Sapatas de Embreagem",
    standardValue: "4,0 a 4,5 mm",
    serviceLimit: "2,0 mm",
    unit: "mm",
    mandatoryAction: "Substituir o conjunto de sapatas de embreagem.",
  },
  {
    id: "lim-valvula-adm",
    item: "Folga de Válvula de Admissão a Frio (< 35°C)",
    standardValue: "0,16 ± 0,02 mm",
    serviceLimit: "Fora de 0,14 a 0,18 mm",
    unit: "mm",
    mandatoryAction: "Regular com lâmina de 0,16 mm justa e contraporca a 10 N.m.",
  },
  {
    id: "lim-valvula-esc",
    item: "Folga de Válvula de Escape a Frio (< 35°C)",
    standardValue: "0,25 ± 0,02 mm",
    serviceLimit: "Fora de 0,23 a 0,27 mm",
    unit: "mm",
    mandatoryAction: "Regular com lâmina de 0,25 mm justa e contraporca a 10 N.m.",
  },
  {
    id: "lim-eletrodo-vela",
    item: "Folga do Eletrodo da Vela (NGK MR8K-9)",
    standardValue: "0,80 a 0,90 mm",
    serviceLimit: "Maior que 0,90 mm",
    unit: "mm",
    mandatoryAction: "Calibrar com cálibre ou substituir vela.",
  },
  {
    id: "lim-pastilhas-freio",
    item: "Espessura do Material de Atrito das Pastilhas",
    standardValue: "4,5 mm",
    serviceLimit: "1,0 mm (fundo da ranhura)",
    unit: "mm",
    mandatoryAction: "Substituir jogo de pastilhas Nissin.",
  },
  {
    id: "lim-disco-freio-diant",
    item: "Espessura da Pista do Disco Dianteiro Wave (Ø 240 mm)",
    standardValue: "4,0 mm",
    serviceLimit: "3,5 mm",
    unit: "mm",
    mandatoryAction:
      "Descarte mandatório se espessura ≤ 3,50 mm (marcado MIN. TH. 3.5 mm). Substituir disco.",
  },
  {
    id: "lim-disco-freio-tras",
    item: "Espessura da Pista do Disco Traseiro Wave (Ø 220 mm)",
    standardValue: "4,0 mm",
    serviceLimit: "3,5 mm",
    unit: "mm",
    mandatoryAction:
      "Descarte mandatório se espessura ≤ 3,50 mm (marcado MIN. TH. 3.5 mm). Substituir disco.",
  },
  {
    id: "lim-empenamento-disco",
    item: "Empenamento Axial Lateral do Disco de Freio (Runout)",
    standardValue: "< 0,05 mm",
    serviceLimit: "0,10 mm",
    unit: "mm",
    mandatoryAction:
      "Aferir com relógio comparador. Se deflexão > 0,10 mm, trocar disco para sanar vibração na frenagem.",
  },
  {
    id: "lim-contra-mola-sec",
    item: "Comprimento Livre da Contra-Mola da Polia Secundária",
    standardValue: "139,5 mm",
    serviceLimit: "134,0 mm",
    unit: "mm",
    mandatoryAction:
      "Substituir mola fadigada 23233-K97-T00 para restaurar pressão de contraste e kick-down.",
  },
  {
    id: "lim-bucha-primaria-de",
    item: "Diâmetro Externo da Bucha da Polia Primária (Boss)",
    standardValue: "23,980 a 23,995 mm",
    serviceLimit: "23,960 mm",
    unit: "mm",
    mandatoryAction: "Substituir bucha deslizante se diâmetro estiver abaixo do limite.",
  },
  {
    id: "lim-polia-movel-di",
    item: "Diâmetro Interno da Face Móvel Primária",
    standardValue: "24,000 a 24,020 mm",
    serviceLimit: "24,060 mm",
    unit: "mm",
    mandatoryAction: "Substituir face móvel da polia primária se folga interna exceder 24,060 mm.",
  },
  {
    id: "lim-pressao-tampa-rad",
    item: "Pressão de Alívio da Tampa Pressurizada do Radiador",
    standardValue: "108 kPa (1,1 kgf/cm² / 15,7 psi)",
    serviceLimit: "Abaixo de 93 kPa ou acima de 123 kPa",
    unit: "kPa",
    mandatoryAction: "Substituir tampa por unidade genuína calibrada a 108 kPa.",
  },
  {
    id: "lim-tensao-bateria",
    item: "Tensão Elétrica de Repouso da Bateria (YTZ6V)",
    standardValue: "≥ 12,8 V",
    serviceLimit: "< 12,4 V (Recarga) / < 10,2 V (Partida)",
    unit: "V",
    mandatoryAction: "Recarregar lentamente a 0,5A ou substituir bateria selada de 12V 5Ah.",
  },
];

export const ADV150_FLUID_SPECS: FluidCapacitySpec[] = [
  {
    id: "fl-oleo-motor",
    fluidName: "Óleo de Motor 4T eSP",
    officialStandard: "SAE 10W-30 API SL JASO MB / JASO T 903 MB (Pro Honda)",
    drainCapacity: "0,80 Litro (800 ml)",
    disassemblyCapacity: "0,90 Litro (900 ml)",
    intervalKm: 6000,
  },
  {
    id: "fl-oleo-redutor",
    fluidName: "Óleo da Transmissão Final (Redutor)",
    officialStandard: "SAE 10W-30 API SL JASO MB ou Óleo de Redutor de Scooter",
    drainCapacity: "0,12 Litro (120 ml)",
    disassemblyCapacity: "0,14 Litro (140 ml)",
    intervalKm: 24000,
  },
  {
    id: "fl-coolant",
    fluidName: "Líquido de Arrefecimento",
    officialStandard:
      "Honda Pro Coolant Tipo 2 (Orgânico OAT 50/50 pronto para uso OEM 08CLA-G01-020)",
    drainCapacity: "0,48 L (radiador) + 0,13 L (expansão) = 0,61 L",
    disassemblyCapacity: "0,61 Litro",
    intervalKm: 36000,
  },
  {
    id: "fl-freio",
    fluidName: "Fluido de Freio Diant./Tras.",
    officialStandard: "DOT 4 sintético de alto ponto de ebulição úmido (> 165°C)",
    drainCapacity: "Substituição por deslocamento contínuo (> 30% reservatório)",
    disassemblyCapacity: "~150 ml por circuito",
    intervalKm: 24000,
  },
  {
    id: "fl-garfo-diant",
    fluidName: "Óleo Hidráulico do Garfo Dianteiro Showa (Bengalas 31 mm)",
    officialStandard: "Honda Fork Oil 10W (Viscosidade SAE 10W, VI ≥ 150)",
    drainCapacity: "~115 ml por bengala",
    disassemblyCapacity: "~115 ml por bengala (coluna de ar superior aferida sem mola)",
    intervalKm: 24000,
  },
];

export const ADV150_OEM_PARTS: OEMPartNumberSpec[] = [
  // Chassi e Estrutura
  {
    id: "oem-chassi-50100",
    partNumber: "50100-K0W-N00",
    name: "Chassi Berço Duplo Monobloco",
    category: "chassi",
    description:
      "Estrutura principal de berço duplo em tubos de aço soldados de alta rigidez torcional.",
  },
  {
    id: "oem-link-motor-50350",
    partNumber: "50350-K0W-N00",
    name: "Link de Articulação do Motor",
    category: "chassi",
    description:
      "Subchassi articulado de fixação oscilante do propulsor ao chassi com buchas antivibração.",
  },
  {
    id: "oem-balanca-52100",
    partNumber: "52100-K0W-N00",
    name: "Balança / Subchassi Direito de Alumínio",
    category: "chassi",
    description:
      "Braço oscilante lateral direito fundido em liga leve de alumínio para fixação do cáliper e amortecedor.",
  },
  {
    id: "oem-presilha-90683",
    partNumber: "90683-GAZ-003",
    name: "Presilhas Plásticas de Expansão Rápida 6 mm (Push-Pins)",
    category: "chassi",
    description:
      "Presilhas de pressão rápida com recuo central de 2 mm para desmontagem de carenagens sem quebras.",
  },

  // Suspensão Showa
  {
    id: "oem-amortecedor-esq-52400",
    partNumber: "52400-K0W-N01",
    name: "Amortecedor Traseiro Showa Piggyback Esquerdo",
    category: "suspensao",
    description:
      "Amortecedor traseiro Showa pressurizado a gás nitrogênio em reservatório externo dourado (curso 120 mm).",
  },
  {
    id: "oem-amortecedor-dir-52500",
    partNumber: "52500-K0W-N01",
    name: "Amortecedor Traseiro Showa Piggyback Direito",
    category: "suspensao",
    description:
      "Amortecedor traseiro Showa pressurizado a gás nitrogênio em reservatório externo dourado (curso 120 mm).",
  },
  {
    id: "oem-garfo-diant-51400",
    partNumber: "51400-K0W-N11",
    name: "Conjunto do Garfo Dianteiro Telescópico Showa 31 mm",
    category: "suspensao",
    description:
      "Bengala dianteira Showa com tubos cromados de 31 mm e curso longo de 130 mm líder da categoria.",
  },

  // Transmissão CVT
  {
    id: "oem-correia-23100",
    partNumber: "23100-K97-T01",
    name: "Correia de Transmissão V-Belt (Bando/Honda)",
    category: "cvt",
    description:
      "Correia dentada com cordonéis de tração em Kevlar/aramida (largura nominal 19,5 mm, limite 18,5 mm).",
  },
  {
    id: "oem-roletes-22123",
    partNumber: "22123-K0W-B00",
    name: "Jogo de Roletes Centrífugos do Variador (6 un x 21g)",
    category: "cvt",
    description:
      "Conjunto balanceado de 6 roletes de 21,0 g cada (20,0 mm nominal, limite 19,5 mm). Operação 100% a seco.",
  },
  {
    id: "oem-face-movel-22110",
    partNumber: "22110-K0W-B00",
    name: "Face Móvel da Polia Primária (Variador)",
    category: "cvt",
    description: "Polia motora móvel usinada em alumínio com pistas para os 6 roletes centrífugos.",
  },
  {
    id: "oem-face-fixa-22102",
    partNumber: "22102-K97-T01",
    name: "Face Fixa da Polia Primária (Ventilador)",
    category: "cvt",
    description:
      "Polia motora fixa em alumínio com aletas de ventilação centrífuga. Travar somente com chave em Y nos orifícios.",
  },
  {
    id: "oem-cursores-22011",
    partNumber: "22011-K97-T00",
    name: "Cursores Plásticos da Placa de Rampa (Slide Pieces - 3 un)",
    category: "cvt",
    description:
      "Guias deslizantes em polímero técnico na rampa de aço; eliminam ruído de chocalho na marcha lenta.",
  },
  {
    id: "oem-bucha-primaria-22105",
    partNumber: "22105-K97-T00",
    name: "Bucha Central da Polia Primária (Drive Face Boss)",
    category: "cvt",
    description:
      "Bucha de aço temperado retificado para deslizamento axial da face móvel (DE nominal 23,980-23,995 mm).",
  },
  {
    id: "oem-porca-virabrequim-90201",
    partNumber: "90201-415-000",
    name: "Porca Flangeada M14 do Virabrequim",
    category: "cvt",
    description:
      "Porca sextavada 22 mm com rosca M14 x 1.25 para retenção do variador dianteiro a 59 N.m.",
  },
  {
    id: "oem-campana-22100",
    partNumber: "22100-K0W-N00",
    name: "Campana da Embreagem Secundária (Clutch Outer 125 mm)",
    category: "cvt",
    description:
      "Campana em aço estampado com diâmetro interno nominal de 125,0 mm (limite de descarte 125,5 mm).",
  },
  {
    id: "oem-sapatas-22535",
    partNumber: "22535-K0W-N00",
    name: "Conjunto de Sapatas da Embreagem Centrífuga (3 un)",
    category: "cvt",
    description:
      "Prato de embreagem com 3 sapatas de lonização a seco (espessura nominal 4,0-4,5 mm, limite 2,0 mm).",
  },
  {
    id: "oem-contra-mola-23233",
    partNumber: "23233-K97-T00",
    name: "Contra-Mola Helicoidal de Contraste da Secundária",
    category: "cvt",
    description:
      "Mola de contraste com pré-carga de 35-50 kgf (comprimento livre 139,5 mm, limite 134,0 mm).",
  },

  // Freios e Segurança
  {
    id: "oem-pastilhas-diant-06455",
    partNumber: "06455-K0W-N01",
    name: "Jogo de Pastilhas de Freio Dianteiro Nissin",
    category: "freios",
    description:
      "Pastilhas genuínas Nissin para cáliper de 2 pistões com ABS monocanal (limite 1,0 mm).",
  },
  {
    id: "oem-pastilhas-tras-06435",
    partNumber: "06435-K0W-N01",
    name: "Jogo de Pastilhas de Freio Traseiro Nissin",
    category: "freios",
    description:
      "Pastilhas genuínas Nissin para cáliper traseiro de pistão simples (limite 1,0 mm).",
  },

  // Motor e Filtragem
  {
    id: "oem-tela-oleo-15421",
    partNumber: "15421-KPL-900",
    name: "Tela / Peneira Filtrante de Óleo Cilíndrica",
    category: "motor",
    description:
      "Malha cilíndrica de aço inox (strainer screen); montar com o lábio de borracha voltado para dentro.",
  },
  {
    id: "oem-mola-tela-15426",
    partNumber: "15426-KVB-900",
    name: "Mola Cônica de Retenção da Tela de Óleo",
    category: "motor",
    description:
      "Mola cônica de sustentação da tela contra a galeria; base larga apoiada na tampa sextavada.",
  },
  {
    id: "oem-tampa-tela-12361",
    partNumber: "12361-035-000",
    name: "Tampa Sextavada 17 mm da Tela de Óleo (M30)",
    category: "motor",
    description: "Tampa de alumínio sextavada 17 mm M30 x 1.5; torque canônico de 20 N.m.",
  },
  {
    id: "oem-oring-tela-91302",
    partNumber: "91302-001-020",
    name: "Anel O-Ring 30,8 x 3 mm da Tampa da Tela de Óleo",
    category: "motor",
    description:
      "Anel elastomérico de vedação térmica da tampa M30; lubrificar com óleo novo antes da montagem.",
  },
  {
    id: "oem-bujao-motor-90131",
    partNumber: "90131-883-000",
    name: "Bujão de Dreno do Óleo do Motor M12",
    category: "motor",
    description:
      "Parafuso flangeado sextavado 12 mm com rosca métrica M12 x 1.5; torque canônico de 24 N.m.",
  },
  {
    id: "oem-junta-cabecote-12391",
    partNumber: "12391-K97-T00",
    name: "Junta de Borracha da Tampa do Cabeçote",
    category: "motor",
    description:
      "Junta de borracha nitrílica moldada com perfil para assentamento das meias-luas do cabeçote.",
  },
  {
    id: "oem-corpo-tbi-16400",
    partNumber: "16400-K0W-N01",
    name: "Corpo de Borboleta TBI Keihin 26 mm",
    category: "motor",
    description:
      "Corpo de aceleração monocorpo com película selante de MoS2 na borboleta; proibido raspar ou usar solventes agressivos.",
  },
  {
    id: "oem-valvula-iacv-16430",
    partNumber: "16430-GFM-902",
    name: "Válvula de Controle de Marcha Lenta (IACV)",
    category: "motor",
    description:
      "Motor de passo para controle da rotação de marcha lenta oficial em 1.700 ± 100 rpm.",
  },
  {
    id: "oem-injetor-16450",
    partNumber: "16450-K0W-N01",
    name: "Eletroinjetor de Combustível PGM-FI (6 furos)",
    category: "motor",
    description:
      "Bico injetor de microatomização para injeção multiponto (resistência 11 a 13 Ω; pressão 294 kPa).",
  },
  {
    id: "oem-sonda-lambda-36532",
    partNumber: "36532-K0W-N01",
    name: "Sensor de Oxigênio Aquecido (Sonda Lambda M12)",
    category: "motor",
    description:
      "Sensor de oxigênio de dióxido de zircônio aquecido rosqueado no escapamento (torque 25 N.m).",
  },

  // Eletroeletrônica e Sensores
  {
    id: "oem-sensor-unit-16060",
    partNumber: "16060-KVS-J01",
    name: "Unidade Integrada de Sensores (TP / MAP / IAT)",
    category: "eletrica",
    description:
      "Módulo integrado com potenciômetro de borboleta (TP), sensor de carga (MAP) e temperatura do ar (IAT).",
  },
  {
    id: "oem-sensor-eot-37870",
    partNumber: "37870-KZR-601",
    name: "Sensor de Temperatura do Óleo do Motor (EOT)",
    category: "eletrica",
    description:
      "Termistor NTC rosqueado no cabeçote (2,5-2,8 kΩ a 20°C); pino central da rotina de calibração TP.",
  },

  // Consumíveis e Elementos Filtrantes
  {
    id: "oem-arruela-motor-94109",
    partNumber: "94109-12000",
    name: "Arruela de Esmagamento de Alumínio 12 mm",
    category: "consumiveis",
    description:
      "Arruela de vedação de uso único para o bujão do cárter M12. Substituição mandatória a cada troca de óleo.",
  },
  {
    id: "oem-arruela-redutor-90495",
    partNumber: "90495-MN5-000",
    name: "Arruela de Vedação de Alumínio 8 mm do Redutor (2 un)",
    category: "consumiveis",
    description:
      "Arruelas de esmagamento para os bujões M8 de nível e dreno da transmissão final traseira.",
  },
  {
    id: "oem-arruela-dreno-coolant-90441",
    partNumber: "90441-ME9-000",
    name: "Arruela de Vedação de Cobre 6,5 mm da Bomba d'Água",
    category: "consumiveis",
    description:
      "Arruela de cobre recozido para o parafuso de dreno M6 da bomba d'água (10 N.m). Uso único.",
  },
  {
    id: "oem-filtro-ar-17210",
    partNumber: "17210-K0W-B00",
    name: "Elemento do Filtro de Ar do Motor (Papel Viscoso)",
    category: "consumiveis",
    description:
      "Elemento filtrante viscoso original. Troca a cada 18.000 km; proibido limpar com ar comprimido.",
  },
  {
    id: "oem-filtro-cvt-17274",
    partNumber: "17274-K0W-N00",
    name: "Elemento de Espuma do Duto de Arrefecimento do CVT",
    category: "consumiveis",
    description:
      "Filtro de espuma de poliuretano lavável na tomada de ar do CVT. Lavar com sabão neutro e secar à sombra.",
  },
  {
    id: "oem-vela-ignicao-31918",
    partNumber: "31918-K97-T01",
    name: "Vela de Ignição NGK MR8K-9 (Rosca M10)",
    category: "consumiveis",
    description:
      "Vela resistiva de grau térmico 8 com gap 0,80 a 0,90 mm. Torque 16 N.m; troca a cada 18.000 km.",
  },
  {
    id: "oem-coolant-pro-08cla",
    partNumber: "08CLA-G01-020",
    name: "Honda Pro Coolant Tipo 2 (Orgânico OAT 50/50)",
    category: "consumiveis",
    description:
      "Líquido de arrefecimento genuíno pronto para uso livre de silicatos/boratos; blinda o selo mecânico cerâmico.",
  },
  {
    id: "oem-graxa-silicone-08c30",
    partNumber: "08C30-B0224M",
    name: "Graxa de Silicone Pura Shin-Etsu G-40M (Pinças Nissin)",
    category: "consumiveis",
    description:
      "Lubrificante puro de silicone para os pinos deslizantes dos cálipers Nissin; 100% inerte contra buchas EPDM.",
  },
];

export const ADV150_SPECIAL_TOOLS: SpecialToolSpec[] = [
  {
    id: "tool-jis-vessel",
    codeOrStandard: "JIS B 1012 (Vessel #2 / Megadora)",
    name: "Chave Cruzada Japonesa JIS #2",
    category: "ferramenta_manual",
    application:
      "Reservatório do cilindro mestre Nissin, punhos de comando e fixações plásticas com cabeça dot-mark.",
    criticalUsageNote:
      "Paredes verticais a 90° e fundo plano; elimina o efeito cam-out. Proibido usar chave Phillips PH2 (risco de espanamento).",
  },
  {
    id: "tool-fixador-y",
    codeOrStandard: "07725-0030000",
    name: "Fixador Universal de Polia em Y (Universal Holder)",
    category: "ferramenta_especial_honda",
    application:
      "Imobilização da face fixa do variador primário e da campana da embreagem secundária.",
    criticalUsageNote:
      "Apoiar exclusivamente nos furos usinados reforçados; proibido apoiar nas aletas para evitar Broken Fin Syndrome e vibração a 8.500 rpm.",
  },
  {
    id: "tool-compressor-mola",
    codeOrStandard: "07LME-GZ40201",
    name: "Compressor de Contra-Mola da Polia Secundária",
    category: "ferramenta_especial_honda",
    application:
      "Compressão e descompressão controlada da mola da embreagem centrífuga (pré-carga de 35 a 50 kgf).",
    criticalUsageNote:
      "Contenção mandatória de segurança contra ejeção balística violenta da porca de 39 mm e das sapatas.",
  },
  {
    id: "tool-soquete-39mm",
    codeOrStandard: "07GMA-KS40100",
    name: "Chave / Soquete Sextavado Plano sem Chanfro 39 mm",
    category: "ferramenta_especial_honda",
    application: "Desaperto e aperto da porca fina M28 da embreagem secundária a 54 N.m.",
    criticalUsageNote:
      "Face usinada plana sem chanfro de entrada; distribui 100% da carga sobre a porca de apenas 4,5 mm de espessura sem escapar.",
  },
  {
    id: "tool-torquimetro-iso6789",
    codeOrStandard: 'ISO 6789 (1/4", 3/8", 1/2")',
    name: "Torquímetros Calibrados de Precisão (Baixa e Alta Escala)",
    category: "instrumento_metrologia",
    application: "Aperto milimétrico de fixadores de motor (5-25 N.m) e eixos/polias (20-120 N.m).",
    criticalUsageNote:
      "Tolerância máx ±4%; cessar força no primeiro estalo; sempre desarmar a mola para a escala mínima após o uso; proibido confundir N.m com lbf.ft (+35,6%).",
  },
  {
    id: "tool-paquimetro-centesimal",
    codeOrStandard: "DIN 862 (0,01 mm)",
    name: "Paquímetro Digital Centesimal Inox (0,01 mm)",
    category: "instrumento_metrologia",
    application:
      "Aferição de desgaste da correia V-Belt (18,5 mm), roletes (19,5 mm), campana (125,5 mm) e espessura de discos de freio (3,5 mm).",
    criticalUsageNote:
      "Metrologia de precisão centesimal sem tolerância para estimativas visuais; leituras cruzadas em 3 a 4 pontos.",
  },
  {
    id: "tool-calibre-laminas",
    codeOrStandard: "Milimétrico Centesimal (0,05 a 1,00 mm)",
    name: "Cálibre de Folga de Lâminas Centesimal",
    category: "instrumento_metrologia",
    application:
      "Regulagem de folga de válvulas a frio (< 35°C: 0,16 mm / 0,10 mm ADM e 0,25 mm / 0,24 mm ESC) e gap da vela (0,80 a 0,90 mm).",
    criticalUsageNote:
      "Deslizamento sob teste de arrasto suave (smooth drag) e validação estrita com método passa / não passa.",
  },
  {
    id: "tool-jumper-scs",
    codeOrStandard: "070PZ-ZY30100",
    name: "Conector Jumper de Serviço SCS para DLC",
    category: "ferramenta_especial_honda",
    application:
      "Diagnóstico de códigos de falha MIL, reset de memória do ECM e calibração de repouso do TP no conector DLC vermelho.",
    criticalUsageNote:
      "Interligar estritamente o fio Marrom (SCS) e Verde/Preto (Terra); proibido encostar no fio Vermelho/Amarelo (+12V DC).",
  },
  {
    id: "tool-chave-vela-16mm",
    codeOrStandard: "Chave de Vela 16 mm Articulada",
    name: "Chave de Vela Articulada com Retentor de Borracha Interno",
    category: "ferramenta_manual",
    application: "Remoção e instalação da vela NGK MR8K-9 no poço estreito do cabeçote.",
    criticalUsageNote:
      "Retentor de borracha protege a cerâmica contra fraturas; rosquear primeiro à mão por 4 a 5 voltas completas.",
  },
  {
    id: "tool-espatulas-nylon",
    codeOrStandard: "Espátula Automotiva de Nylon",
    name: "Jogo de Espátulas Plásticas de Nylon para Carenagens",
    category: "ferramenta_manual",
    application: "Desencaixe de travas macho-fêmea das carenagens plásticas no túnel e assoalho.",
    criticalUsageNote:
      "Alavancagem suave longitudinal sem riscar o verniz automotivo nem deformar as linguetas plásticas.",
  },
];

export const ADV150_VEHICLE_IDENTITY: VehicleIdentitySpec = {
  owner: {
    name: "ALEX SANTANA MENDES",
    cpfMasked: "397.417.818-06",
    cityState: "SAO PAULO SP",
  },
  registration: {
    plate: "STW8G80",
    renavam: "01370476075",
    crv: "233885130718",
    claSecurityCode: "14688463030",
    category: "PARTICULAR",
    speciesType: "PASSAGEIRO MOTONETA",
    fuel: "GASOLINA",
    color: "VERMELHA",
    grossWeightKg: 310,
    capacity: "02P",
    issueDate: "01/03/2025",
    exerciseYear: 2024,
  },
  vehicle: {
    brandModelVersion: "HONDA/ADV 150",
    projectCode: "K0W",
    modelCode: "KF43",
    vin: "9C2KF4300RR003710",
    vinDecoded: {
      wmi: "9C2",
      vds: "KF430",
      checkDigit: "0",
      modelYearCode: "R",
      plantCode: "R",
      serialNumber: "003710",
    },
    engineNumber: "KF43E0R003718",
    engineFamily: "KF43E",
    manufactureYear: 2023,
    modelYear: 2024,
    displacementCc: 149.3,
    maxPowerCv: 13.2,
  },
};

export const ADV150_CVT_DURABILITY_SPECS: CvtDurabilitySpec[] = [
  {
    id: "cvt-dur-correia",
    component: "Correia de Transmissão V-Belt (Bando/Honda OEM)",
    partNumber: "23100-K97-T01",
    officialDurabilityKm: 24000,
    severeDurabilityKm: "18.000 a 24.000 km",
    standardValue: "19,5 mm",
    serviceLimit: "18,5 mm (ou trincas na raiz dos dentes)",
    unit: "mm",
    symptoms:
      "Perda de 5 a 15 km/h de velocidade final; alta rotação para velocidade de cruzeiro; trincas no teste de flexão reversa.",
    preventiveAction:
      "Inspeção visual a cada 8.000 a 12.000 km; substituição preventiva mandatória aos 24.000 km.",
  },
  {
    id: "cvt-dur-roletes",
    component: "Jogo de Roletes Centrífugos (6 un x 21,0 g)",
    partNumber: "22123-K0W-B00",
    officialDurabilityKm: 24000,
    severeDurabilityKm: "12.000 a 18.000 km",
    standardValue: "20,0 mm / 21,0 g cada",
    serviceLimit: "19,5 mm (ou qualquer faceta plana visível)",
    unit: "mm",
    symptoms:
      "Hesitação e buraco na aceleração entre 30 e 50 km/h; aceleração não linear; perda de arrancada.",
    preventiveAction:
      "Trabalham 100% a seco (proibição absoluta de graxa); substituição do jogo completo de 6 unidades.",
  },
  {
    id: "cvt-dur-cursores",
    component: "Cursores Plásticos da Placa de Rampa (Slide Pieces - 3 un)",
    partNumber: "22011-K97-T00",
    officialDurabilityKm: 24000,
    severeDurabilityKm: "12.000 a 18.000 km",
    standardValue: "Ajuste justo sem folga lateral",
    serviceLimit: "Folga lateral > 0,2 mm na rampa de aço",
    unit: "mm",
    symptoms:
      "Ruído metálico irregular de chocalho na marcha lenta a 1.500 rpm; ruído some imediatamente ao acelerar.",
    preventiveAction:
      "Substituir os 3 cursores a cada revisão do variador ou troca de correia por custo irrisório.",
  },
  {
    id: "cvt-dur-face-movel",
    component: "Face Móvel da Polia Primária (Variador de Alumínio)",
    partNumber: "22110-K0W-B00",
    officialDurabilityKm: 48000,
    severeDurabilityKm: "36.000 a 48.000 km",
    standardValue: "24,000 a 24,020 mm (DI do furo central)",
    serviceLimit: "24,060 mm (ou formação de degrau nas pistas)",
    unit: "mm",
    symptoms:
      "Degrau na transição de marcha; aceleração em ondas; trepidação nas polias dianteiras.",
    preventiveAction:
      "Limpeza periódica com álcool isopropílico 99%; proibição de graxa que desgasta as rampas com pó abrasivo.",
  },
  {
    id: "cvt-dur-bucha",
    component: "Bucha Central da Polia Primária (Drive Face Boss)",
    partNumber: "22105-K97-T00",
    officialDurabilityKm: 48000,
    severeDurabilityKm: "48.000 a 72.000 km",
    standardValue: "23,980 a 23,995 mm (DE da bucha)",
    serviceLimit: "23,960 mm (folga diametral máxima 0,10 mm)",
    unit: "mm",
    symptoms: "Folga excessiva entre bucha e face móvel; oscilação axial do variador dianteiro.",
    preventiveAction:
      "Limpar com álcool isopropílico; inspecionar pistas temperadas quanto a riscos ou engripamento.",
  },
  {
    id: "cvt-dur-face-fixa",
    component: "Face Fixa da Polia Primária (Ventilador com Aletas)",
    partNumber: "22102-K97-T01",
    officialDurabilityKm: 72000,
    severeDurabilityKm: "48.000 a 72.000 km",
    standardValue: "Superfície cônica plana e estrias intactas",
    serviceLimit: "Estrias desgastadas ou aletas de refrigeração quebradas",
    unit: "un",
    symptoms:
      "Vibração extrema a 8.500 rpm por quebra de aleta de ventilação; roletamento sobre as estrias do virabrequim.",
    preventiveAction:
      "Travar exclusivamente com fixador em Y 07725-0030000 nos orifícios reforçados; proibido apoiar nas aletas.",
  },
  {
    id: "cvt-dur-campana",
    component: "Campana da Embreagem Secundária (Clutch Outer 125 mm)",
    partNumber: "22100-K0W-N00",
    officialDurabilityKm: 48000,
    severeDurabilityKm: "36.000 a 48.000 km",
    standardValue: "125,0 mm (DI da pista de atrito)",
    serviceLimit: "125,5 mm (ou coloração azulada de têmpera)",
    unit: "mm",
    symptoms:
      "Trepidação violenta na arrancada de 0 a 20 km/h; patinação e superaquecimento; sulcos axiais profundos.",
    preventiveAction:
      "Limpeza a seco e lixamento com lixa d'água 220 a cada 12.000 km; evitar pilotar segurando freio e acelerando.",
  },
  {
    id: "cvt-dur-sapatas",
    component: "Conjunto de Sapatas da Embreagem Centrífuga (3 un)",
    partNumber: "22535-K0W-N00",
    officialDurabilityKm: 36000,
    severeDurabilityKm: "18.000 a 24.000 km",
    standardValue: "4,0 a 4,5 mm de espessura de lona",
    serviceLimit: "2,0 mm de espessura mínima",
    unit: "mm",
    symptoms:
      "Patinação na arrancada; rotação sobe desproporcionalmente sem transferir torque para a roda traseira.",
    preventiveAction:
      "Desvitrificação preventiva suave a cada 12.000 km com lixa d'água 220; substituição do conjunto completo.",
  },
  {
    id: "cvt-dur-contra-mola",
    component: "Contra-Mola Helicoidal da Polia Secundária",
    partNumber: "23233-K97-T00",
    officialDurabilityKm: 48000,
    severeDurabilityKm: "24.000 a 36.000 km",
    standardValue: "139,5 mm de comprimento livre descarregada",
    serviceLimit: "134,0 mm de comprimento livre mínimo",
    unit: "mm",
    symptoms:
      "Perda de força em subidas e arrancadas pesadas; secundária abre prematuramente por fadiga elástica da mola.",
    preventiveAction:
      "Medir comprimento livre no compressor de embreagem 07LME-GZ40201 a cada troca de correia.",
  },
  {
    id: "cvt-dur-torque-cam",
    component: "Pinos Guia e Roletes de Aço do Torque Cam",
    partNumber: "23225-KS4-000",
    officialDurabilityKm: 48000,
    severeDurabilityKm: "48.000 a 72.000 km",
    standardValue: "Cilindros perfeitamente retificados",
    serviceLimit: "Presença de calos, amassamento ou ranhuras",
    unit: "mm",
    symptoms:
      "Transição de marcha áspera; travamento no kick-down mecânico ao exigir torque em subidas.",
    preventiveAction:
      "Desmontar, lavar e relubrificar com graxa de poliureia com MoS2 a cada 24.000 km.",
  },
  {
    id: "cvt-dur-vedacoes-tcam",
    component: "Retentores de Viton e Anéis O-Ring do Torque Cam (Kit)",
    partNumber: "91211-KN7-671",
    officialDurabilityKm: 24000,
    severeDurabilityKm: "24.000 km (substituição mandatória)",
    standardValue: "Lábio duplo de viton com mola e O-rings elásticos",
    serviceLimit: "Ressecamento, perda de elasticidade ou vazamento",
    unit: "un",
    symptoms:
      "Graxa derretida vaza para a câmara do CVT, contaminando as polias cônicas, correia e lonas de embreagem.",
    preventiveAction:
      "Substituição mandatória a cada abertura do Torque Cam para reengraxamento (24.000 km).",
  },
  {
    id: "cvt-dur-graxa-cvt",
    component: "Graxa Especial de Poliureia com MoS2 para CVT",
    partNumber: "08798-9002",
    officialDurabilityKm: 24000,
    severeDurabilityKm: "18.000 a 24.000 km",
    standardValue: "Graxa de poliureia com Bissulfeto de Molibdênio (> 260°C)",
    serviceLimit: "Carbonização, perda de consistência ou separação de óleo",
    unit: "°C",
    symptoms:
      "Graxa de sabão de lítio comum vira óleo líquido a 120°C e vence os retentores, banhando a correia.",
    preventiveAction:
      "Utilizar exclusivamente graxa com espessante de poliureia e aditivo de MoS2 com ponto de gota > 260°C.",
  },
  {
    id: "cvt-dur-rolamentos-sec",
    component: "Rolamentos da Polia Secundária (Agulhas e Esferas)",
    partNumber: "91001-KCW-003",
    officialDurabilityKm: 48000,
    severeDurabilityKm: "48.000 a 72.000 km",
    standardValue: "Giro perfeitamente suave e silencioso",
    serviceLimit: "Folga radial perceptível, aspereza ou ruído de esferas secas",
    unit: "un",
    symptoms:
      "Ruído contínuo de rolamento roncando ao movimentar a moto desligada no cavalete central.",
    preventiveAction:
      "Inspecionar giro livre e ausência de folga a cada revisão de 24.000 km; substituir o par se áspero.",
  },
  {
    id: "cvt-dur-espuma-cvt",
    component: "Elemento de Espuma do Duto de Arrefecimento do CVT",
    partNumber: "17274-K0W-N00",
    officialDurabilityKm: 24000,
    severeDurabilityKm: "24.000 km",
    standardValue: "Espuma de poliuretano elástica e limpa",
    serviceLimit: "Esfarelamento, rasgos ou perda de elasticidade",
    unit: "un",
    symptoms:
      "Entrada de poeira e areia na câmara do CVT, causando abrasão acelerada das polias de alumínio.",
    preventiveAction:
      "Lavar com água e sabão neutro a cada 6.000 a 12.000 km; secar 100% à sombra; instalar rigorosamente a seco.",
  },
  {
    id: "cvt-dur-junta-tampa",
    component: "Junta de Vedação Perimetral da Carcaça do CVT",
    partNumber: "11395-K97-T00",
    officialDurabilityKm: 24000,
    severeDurabilityKm: "2 a 3 aberturas da carcaça",
    standardValue: "Perfil elastomérico contínuo flexível",
    serviceLimit: "Esmagamento permanente, cortes ou ressecamento",
    unit: "un",
    symptoms:
      "Entrada de água de chuva ou lama na câmara do CVT em enchentes, provocando patinação imediata da correia.",
    preventiveAction:
      "Substituir ao notar deformação ou corte; assegurar alinhamento nos 2 pinos guia de aço de 10x16 mm.",
  },
  {
    id: "cvt-dur-rolamento-tampa",
    component: "Rolamento de Apoio da Tampa do CVT (Esferas 6002)",
    partNumber: "96100-60020-00",
    officialDurabilityKm: 48000,
    severeDurabilityKm: "48.000 a 72.000 km",
    standardValue: "Rolamento blindado radial 6002 com giro suave",
    serviceLimit: "Folga radial perceptível ou giro travando",
    unit: "un",
    symptoms:
      "Vibração e ressonância na carcaça esquerda; folga na extremidade do eixo primário da transmissão.",
    preventiveAction:
      "Testar rotação suave com o dedo na tampa a cada abertura do CVT; lubrificar ou substituir se áspero.",
  },
];
